import moment from 'moment';
import {
  ParticularTravelsFiltersFormType,
  StatusPayment
} from '../models/particularTravelsModel';
import { SortState, TabletReactQueryState } from 'shared/models/tabletReactQuery';

export interface ParticularColumnVisibility {
  id: boolean;
  cliente: boolean;
  trip_end_time: boolean;
  amount: boolean;
  status_payment: boolean;
  puntos: boolean;
  coordinationObservation: boolean;
  general_observation: boolean;
  service: boolean;
  assigned_driver: boolean;
  passengers: boolean;
  status: boolean;
  has_required_bill: boolean;
  actions: boolean;
}

export const columnsLabels: Record<keyof ParticularColumnVisibility, string> = {
  id: 'ID',
  cliente: 'Cliente',
  trip_end_time: 'Finalizado a las',
  amount: 'Monto',
  status_payment: 'Estado de pago',
  puntos: 'Domicilios',
  coordinationObservation: 'Obs. Coordinación',
  general_observation: 'Obs. Chofer',
  service: 'Servicio',
  assigned_driver: 'Chofer',
  passengers: 'Pasajeros',
  status: 'Facturado',
  has_required_bill: 'Factura requerida',
  actions: 'Acciones'
};

export interface ParticularTravelsState extends TabletReactQueryState {
  filters: ParticularTravelsFiltersFormType;
  columnVisibility: ParticularColumnVisibility;
  lastAccessDate?: string;
}

const STORAGE_KEY = 'particularTravelsState';

export const getDefaultFilters = (): ParticularTravelsFiltersFormType => ({
  id: null,
  state: false,
  status_payment: StatusPayment.UNPAID,
  clientId: null,
  dateFrom: moment().startOf('month'),
  dateTo: moment().endOf('month'),
  has_required_bill: null,
  has_send_bill: null
});

export const getDefaultColumnVisibility = (): ParticularColumnVisibility => ({
  id: true,
  cliente: true,
  trip_end_time: true,
  amount: true,
  status_payment: true,
  puntos: false,
  coordinationObservation: false,
  general_observation: false,
  service: false,
  assigned_driver: false,
  passengers: false,
  status: true,
  has_required_bill: true,
  actions: true
});

export const getDefaultSort = (): SortState => ({
  sortDir: 'ASC',
  sortBy: 'trip_end_time'
});

export const getDefaultState = (): ParticularTravelsState => ({
  filters: getDefaultFilters(),
  page: 1,
  rowsPerPage: 10,
  sort: getDefaultSort(),
  columnVisibility: getDefaultColumnVisibility()
});

const deserializeState = (
  storedState: Partial<ParticularTravelsState>
): ParticularTravelsState => {
  const defaultFilters = getDefaultFilters();
  const today = moment().format('YYYY-MM-DD');
  const lastAccessDate = storedState.lastAccessDate;
  const isNewDay = !lastAccessDate || lastAccessDate !== today;


  return {
    ...getDefaultState(),
    ...storedState,
    filters: {
      ...defaultFilters,
      ...(storedState.filters || {}),
      dateFrom: isNewDay
        ? moment().startOf('month')
        : storedState.filters?.dateFrom
        ? moment(storedState.filters.dateFrom)
        : moment().startOf('month'),
      dateTo: isNewDay
        ? moment().endOf('month')
        : storedState.filters?.dateTo
        ? moment(storedState.filters.dateTo)
        : moment().endOf('month')
    },
    lastAccessDate: today // Actualizar la fecha de acceso
  };
};

export const loadParticularStateFromLocalStorage =
  (): ParticularTravelsState => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return deserializeState(parsed);
      }
    } catch (error) {
      console.error('Error loading particular travels state:', error);
    }
    return getDefaultState();
  };

export const saveParticularStateToLocalStorage = (
  state: ParticularTravelsState
): void => {
  try {
    const stateWithDate = {
      ...state,
      lastAccessDate: moment().format('YYYY-MM-DD')
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stateWithDate));
  } catch (error) {
    console.error('Error saving particular travels state:', error);
  }
};

export const clearParticularStateFromLocalStorage = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing particular travels state:', error);
  }
};

